import { isGpnsReportKey } from '@/features/internal/reports/marriott-gpns/types'
import { complianceProgramBySlug } from '@/lib/compliance/programs'
import {
  type ParentBreadcrumb,
  type SectionConfig
} from '@/utils/get-matched-section'

import { GPNS_REPORT_LABELS } from '@wci/wv-db-schemas/reports'

const REPORTS_BREADCRUMB: ParentBreadcrumb = {
  label: 'Reports',
  href: (_params, orgSlug) => `/${orgSlug}/internal/reports`
}

export const sectionConfig: SectionConfig = {
  '/internal/search': {
    title: 'Advanced Search'
  },
  // Utilities (internal config tools moved here from the Admin app)
  '/internal/utilities/hive-projects': {
    title: 'Hive User Projects',
    description: 'Manage user access to Hive projects',
    showBreadcrumbs: false
  },
  '/internal/utilities/pricing': {
    title: 'Pricing',
    description:
      'Manage price books and import vendor price lists for the design team.',
    showBreadcrumbs: false
  },
  '/internal/utilities/channel-impact': {
    title: 'Channel Impact',
    description:
      'Find every property carrying a Dish channel — for triaging a takedown, blackout or lineup change.',
    showBreadcrumbs: false
  },
  '/internal/utilities/mission-control': {
    title: 'Mission Control',
    description:
      'Every always-on AI agent: whether it is working, what its last cycle did, and who owns it.',
    showBreadcrumbs: false
  },
  '/internal/utilities/contact-review': {
    title: 'Contact Review',
    description:
      'Decide which contacts are the same person across ServiceNow, Business Central, Dynamics and Salesforce.',
    showBreadcrumbs: false
  },
  '/internal/utilities/initiatives': {
    title: 'Initiatives',
    description:
      'Group customers into organization-wide initiatives surfaced on their dashboards.',
    showBreadcrumbs: false
  },
  '/internal/dashboard': {
    title: (_params, org) =>
      org?.name ? `${org.name} Dashboard` : 'Organization Dashboard'
  },
  '/internal/map': {
    fullBleed: true,
    title: 'Map'
  },
  '/internal/properties': {
    title: 'Properties'
  },
  '/internal/fleet-lens': {
    title: 'Fleet Lens',
    description:
      'Read-only, customer-scoped fleet, compliance and lifecycle figures — every number drills to its devices.'
  },
  '/internal/compliance/[program]': {
    title: (params) => {
      const { program } = params as { program?: string }
      const p = program ? complianceProgramBySlug(program) : undefined
      return p ? `${p.brand} · ${p.standard} compliance` : 'Compliance'
    },
    description:
      'Fiber, bandwidth and evidence for every hotel in the program, WorldVue-supported and self-reporting.'
  },
  '/internal/projects': {
    title: 'Projects'
  },
  '/internal/surveys': {
    title: 'CSAT Surveys'
  },
  '/internal/technicians': {
    title: 'Technicians'
  },
  '/internal/companies': {
    title: 'Companies',
    description:
      'Brands, management companies, ownership groups and technician companies, linked to the properties they run.'
  },
  '/internal/companies/[companyId]': {
    title: 'Company Details',
    parentBreadcrumb: {
      label: 'Companies',
      href: (_params, orgSlug) => `/${orgSlug}/internal/companies`
    }
  },
  '/internal/companies/review': {
    title: 'Company Review Queue',
    description:
      'Unmatched values, duplicate FT numbers and link changes waiting on a decision.',
    parentBreadcrumb: {
      label: 'Companies',
      href: (_params, orgSlug) => `/${orgSlug}/internal/companies`
    }
  },
  '/internal/products': {
    title: 'Products',
    description:
      'The product catalog, how many properties use each product, and the services-sold values not yet mapped.'
  },
  '/internal/products/[productId]': {
    title: 'Product Details',
    parentBreadcrumb: {
      label: 'Products',
      href: (_params, orgSlug) => `/${orgSlug}/internal/products`
    }
  },
  '/internal/technicians/[techId]': {
    title: (params) => {
      const routeParams = params as { techId?: string }

      if (routeParams?.techId) {
        return `${routeParams.techId} • Technician Details`
      }

      return 'Technician Details'
    },
    parentBreadcrumb: {
      label: 'Technicians',
      href: (_params, orgSlug) => {
        return `/${orgSlug}/internal/technicians`
      }
    }
  },
  '/internal/technicians/[techId]/special-notes': {
    title: 'Special Notes History',
    parentBreadcrumb: {
      label: (params) => {
        const { techId } = params as { techId?: string }
        return techId ? `${techId}` : 'Technician'
      },
      href: (params, orgSlug) => {
        const { techId } = params as { techId?: string }
        return `/${orgSlug}/internal/technicians/${techId}`
      }
    }
  },
  // Presence needs no declaration here: every `/internal/[id]/…` route derives
  // a `customer:<id>` room from its `[id]` param, so the overview, the
  // networking cluster and the room grid already share one. See
  // `src/lib/presence/scopes.ts`.
  '/internal/[id]/overview': {
    title: 'Overview',
    usesHubServer: true, // Enables auto-selection logic
    supportsMultiServer: true, // Allows "All Servers" mode
    showServerSelector: false // Hides header dropdown (HubStatsSection has custom placement)
  },
  '/internal/[id]/field-data': {
    title: 'Field Data',
    description:
      'What technicians measured at this property, compared across visits: speed tests by location, Wi-Fi and RF readings, cable tests and numbers.'
  },
  '/internal/[id]/installs': {
    title: 'Installs',
    description:
      'Devices technicians registered at install time, reconciled against what BC shows as shipped.'
  },
  '/internal/[id]/ota-coverage': {
    title: 'OTA Coverage',
    description:
      'Broadcast stations this property can receive over the air, ranked by signal — our model beside the rabbitears coverage map.'
  },
  '/internal/[id]/spaces': {
    title: 'Spaces',
    description:
      "Racks and cabinets in each of the property's spaces: what is mounted where, and what still needs identifying."
  },
  '/internal/[id]/spaces/labels': {
    title: 'Rack labels',
    description: 'Print QR labels for the racks in this property.'
  },
  '/internal/[id]/history': {
    title: 'Special Notes History'
  },
  '/internal/[id]/networking/overview': {
    title: 'Networking'
  },
  '/internal/[id]/networking/isp-circuits': {
    title: 'ISP Circuits'
  },
  '/internal/[id]/networking/vlans': {
    title: 'VLANs'
  },
  '/internal/[id]/networking/dhcp-scopes': {
    title: 'DHCP Scopes'
  },
  '/internal/[id]/networking/vendor-ips': {
    title: 'Vendor IPs'
  },
  '/internal/[id]/gateway/overview': {
    title: 'Gateway'
  },
  '/internal/[id]/gateway/clients': {
    title: 'Gateway Clients'
  },
  '/internal/[id]/gateway/bypasses': {
    title: 'Gateway Bypasses'
  },
  '/internal/[id]/gateway/authentication': {
    title: 'Gateway Authentication'
  },
  '/internal/[id]/gateway/access-policy': {
    title: 'Gateway Access Policy'
  },
  '/internal/[id]/gateway/vlans': {
    title: 'Gateway VLANs'
  },
  '/internal/[id]/gateway/system': {
    title: 'Gateway System'
  },
  '/internal/[id]/networking/guest-clients': {
    title: 'Guest Clients'
  },
  '/internal/[id]/networking/access-points': {
    title: 'Access Points'
  },
  '/internal/[id]/networking/switches': {
    title: 'Switches'
  },
  '/internal/[id]/networking/appliances': {
    title: 'Appliances'
  },
  '/internal/[id]/networking/topology': {
    fullBleed: true,
    title: 'Network Topology'
  },
  '/internal/[id]/networking/cable-modems': {
    title: 'Cable Modems',
    usesHubServer: true,
    supportsMultiServer: true,
    requiredFeature: 'hasCableModems'
  },
  '/internal/[id]/networking/cable-modems/[serverIp]/[macAddress]': {
    title: 'Cable Modem Details',
    breadcrumb: 'Details',
    parentBreadcrumb: {
      label: 'Cable Modems',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/networking/cable-modems`
    },
    tabs: [
      {
        value: 'overview',
        label: 'Overview',
        entity: 'devices:cable-modems',
        action: 'read'
      },
      {
        value: 'graphs',
        label: 'Graphs',
        entity: 'devices:cable-modems',
        action: 'read'
      }
    ]
  },
  '/internal/[id]/content/channels': {
    title: 'Channels'
  },
  '/internal/[id]/content/videos': {
    title: 'Videos',
    usesHubServer: true,
    supportsMultiServer: false
  },
  '/internal/[id]/tv-experience/displays': {
    title: 'Displays',
    usesHubServer: true,
    supportsMultiServer: true
  },
  '/internal/[id]/tv-experience/displays/[serverIp]/[serial]': {
    title: 'Display Details',
    breadcrumb: 'Details',
    parentBreadcrumb: {
      label: 'Displays',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/tv-experience/displays`
    },
    tabs: [
      {
        value: 'general',
        label: 'General',
        entity: 'devices:displays',
        action: 'read'
      }
    ]
  },
  '/internal/[id]/tv-experience/pms': {
    title: 'PMS',
    usesHubServer: true,
    supportsMultiServer: true
  },
  '/internal/[id]/tv-experience/pms/[serverIp]/configure': {
    fullBleed: true,
    // Title (used for the browser tab) keeps "PMS Configuration"; the
    // breadcrumb override below renders just "Configuration" as the
    // current-page label so it doesn't duplicate the clickable PMS
    // parent breadcrumb.
    title: 'PMS Configuration',
    breadcrumb: 'Configuration',
    parentBreadcrumb: {
      label: 'PMS',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/tv-experience/pms`
    }
  },
  '/internal/[id]/tv-experience/casting': {
    // The casting surface carries its own per-hub picker (casting state is
    // per hub server), so no usesHubServer header selector.
    title: 'Casting'
  },
  '/internal/[id]/tv-experience/casting/manage': {
    title: 'Casting Console',
    parentBreadcrumb: {
      label: 'Casting',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/tv-experience/casting`
    }
  },
  '/internal/[id]/tv-experience/guide': {
    fullBleed: true,
    title: 'Guide',
    usesHubServer: true,
    supportsMultiServer: false,
    showServerSelector: false
  },
  '/internal/[id]/tv-experience/tlx': {
    fullBleed: true,
    title: 'TLX Configuration',
    usesHubServer: true,
    supportsMultiServer: false
  },
  // Every hub gets its own card with its own Identity tab, so the header's
  // hub selector has nothing to drive here.
  '/internal/[id]/tv-experience/server': {
    fullBleed: true,
    title: 'Server',
    usesHubServer: false
  },
  '/internal/[id]/tv-experience/app-config': {
    fullBleed: true,
    title: 'Application Configuration',
    usesHubServer: true,
    supportsMultiServer: false
  },
  '/internal/[id]/room-grid': {
    fullBleed: true,
    title: 'Room Grid',
    usesHubServer: true,
    supportsMultiServer: false,
    requiredFeature: 'hasRoomGrid'
  },
  // The Reporting landing page; every live report's "Reports" breadcrumb links
  // here.
  '/internal/reports': {
    title: 'Reports'
  },
  '/internal/reports/schedules': {
    title: 'Schedules',
    description: 'Reports that run on a schedule.',
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/reports/runs/[id]': {
    title: 'Run',
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/reports/maintenance': {
    title: 'Maintenance',
    description: 'Run-file retention and failure alerts for report schedules.',
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/reports/schedules/[id]': {
    title: 'Schedule',
    parentBreadcrumb: [
      REPORTS_BREADCRUMB,
      {
        label: 'Schedules',
        href: (_params, orgSlug) => `/${orgSlug}/internal/reports/schedules`
      }
    ]
  },
  '/internal/reports/marriott/[report]': {
    title: (params) => {
      const { report } = params as { report?: string }
      return report && isGpnsReportKey(report)
        ? GPNS_REPORT_LABELS[report]
        : 'Report'
    },
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/reports/software-compliance': {
    title: 'Software Compliance',
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/reports/service-mix': {
    fullBleed: true,
    title: 'Service Mix Distribution',
    parentBreadcrumb: REPORTS_BREADCRUMB
  },
  '/internal/[id]/reports': {
    title: (_params, _data, search) => {
      const labels: Record<string, string> = {
        'end-of-life': 'End-of-Life Devices'
      }
      const sub = labels[search?.type ?? 'end-of-life']
      return sub ? `Reports • ${sub}` : 'Reports'
    }
  },
  '/internal/[id]/account/networking': {
    title: 'Networking'
  },
  '/internal/[id]/account/projects': {
    title: 'Projects'
  },
  '/internal/[id]/account/design-tasks': {
    title: 'Design Tasks'
  },
  '/internal/[id]/account/projects/[jobNo]': {
    // Use the job number as the breadcrumb leaf — the project's
    // human name (property + description) lives in the page header
    // strip, so the breadcrumb stays compact and predictable.
    title: (params) => {
      const { jobNo } = params as { jobNo?: string }
      return jobNo ? `${jobNo}` : 'Project Details'
    },
    parentBreadcrumb: {
      label: 'Projects',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/projects`
    }
  },
  '/internal/[id]/account/dispatches': {
    title: 'Dispatches'
  },
  '/internal/[id]/account/files': {
    title: 'Files'
  },
  '/internal/[id]/account/orders': {
    title: 'Orders'
  },
  '/internal/[id]/account/orders/sales/[orderNumber]': {
    title: 'Sales Order Details',
    breadcrumb: 'Sales Order Details',
    parentBreadcrumb: {
      label: 'Orders',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/orders`
    }
  },
  '/internal/[id]/account/orders/service/[orderNumber]': {
    title: 'Service Order Details',
    breadcrumb: 'Service Order Details',
    parentBreadcrumb: {
      label: 'Orders',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/orders`
    }
  },
  '/internal/[id]/account/shipments': {
    title: 'Shipments'
  },
  '/internal/[id]/account/shipments/sales/[shipmentNumber]': {
    title: 'Sales Shipment Details',
    breadcrumb: 'Sales Shipment Details',
    parentBreadcrumb: {
      label: 'Shipments',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/shipments`
    }
  },
  '/internal/[id]/account/shipments/service/[shipmentNumber]': {
    title: 'Service Shipment Details',
    breadcrumb: 'Service Shipment Details',
    parentBreadcrumb: {
      label: 'Shipments',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/shipments`
    }
  },
  '/internal/[id]/account/contracts': {
    title: 'Contracts'
  },
  '/internal/[id]/account/customer-info': {
    title: 'Customer Information'
  },
  '/internal/[id]/account/contacts/[contactId]': {
    title: 'Contact',
    parentBreadcrumb: {
      label: 'Contacts',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/account/contacts`
    }
  },
  '/internal/support/issues': {
    title: 'Issues'
  },
  '/internal/support/voice-agent': {
    fullBleed: true,
    title: 'Mia AI',
    parentBreadcrumb: {
      label: 'Support'
    }
  },
  '/internal/support/voice-agent/overview': {
    fullBleed: true,
    title: 'Overview',
    parentBreadcrumb: {
      label: 'Mia AI',
      href: (_p, orgSlug) =>
        `/${orgSlug ?? ''}/internal/support/voice-agent/overview`
    }
  },
  '/internal/support/voice-agent/calls': {
    fullBleed: true,
    title: 'Calls',
    parentBreadcrumb: {
      label: 'Mia AI',
      href: (_p, orgSlug) =>
        `/${orgSlug ?? ''}/internal/support/voice-agent/overview`
    }
  },
  '/internal/support/voice-agent/configuration': {
    fullBleed: true,
    title: 'Configuration',
    parentBreadcrumb: {
      label: 'Mia AI',
      href: (_p, orgSlug) =>
        `/${orgSlug ?? ''}/internal/support/voice-agent/overview`
    }
  },
  '/internal/support/voice-agent/calls/[callSid]': {
    fullBleed: true,
    title: (params) => {
      const { callSid } = params as { callSid?: string }
      return callSid ? `Call ${callSid}` : 'Call'
    },
    parentBreadcrumb: {
      label: 'Mia AI',
      href: (_p, orgSlug) =>
        `/${orgSlug ?? ''}/internal/support/voice-agent/overview`
    }
  },
  '/internal/support/noc-dashboard': {
    title: 'NOC Dashboard'
  },
  '/internal/support/call-center': {
    title: 'Call Center Overview'
  },
  '/internal/support/links': {
    title: 'Links'
  },
  '/internal/support/incidents': {
    title: 'Incidents'
  },
  '/internal/support/cases': {
    title: 'Cases'
  },
  '/internal/support/cases/[caseId]': {
    title: (params) => {
      const routeParams = params as { caseId?: string }
      if (routeParams?.caseId) {
        return `${routeParams.caseId} • Triage`
      }
      return 'Triage'
    },
    parentBreadcrumb: {
      label: 'Cases',
      href: (_params, orgSlug) => `/${orgSlug}/internal/support/cases`
    }
  },
  '/internal/opportunities': {
    title: 'Opportunities'
  },
  '/internal/opportunities/[opportunityId]': {
    fullBleed: true,
    title: (params) => {
      const routeParams = params as { opportunityId?: string }
      if (routeParams?.opportunityId) {
        return `${routeParams.opportunityId} • Summary`
      }
      return 'Summary'
    },
    tabKey: 'opportunity',
    parentBreadcrumb: {
      label: 'Opportunities',
      href: (_params, orgSlug) => `/${orgSlug}/internal/opportunities`
    }
  },
  '/internal/support/work-orders': {
    title: 'Work Orders'
  },
  '/internal/support/work-order-requests': {
    title: 'Work Order Requests'
  },
  '/internal/support/work-order-tasks': {
    title: 'Work Order Tasks'
  },
  '/internal/[id]/support/issues': {
    title: 'Issues'
  },
  '/internal/[id]/support/incidents': {
    title: 'Incidents'
  },
  '/internal/[id]/support/incidents/[incidentId]': {
    title: (params, data) => {
      const routeParams = params as { id?: string; incidentId?: string }
      if (routeParams?.incidentId) {
        return `${routeParams.incidentId} • Details`
      }
      const incidentData = data as { incidentId?: string; id?: string }
      if (incidentData?.incidentId) {
        return `${incidentData.incidentId} • Details`
      }
      if (incidentData?.id) {
        return `${incidentData.id} • Details`
      }
      return 'Details'
    },
    tabKey: 'incident',
    parentBreadcrumb: {
      label: 'Incidents',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/support/incidents`
    }
  },
  '/internal/[id]/support/cases': {
    title: 'Cases'
  },
  '/internal/[id]/support/interactions': {
    title: 'Interactions'
  },
  '/internal/[id]/support/links': {
    title: 'Links'
  },
  '/internal/[id]/support/cases/[caseId]': {
    // Full-bleed so the h-full flex chain inside CaseDetails resolves to a
    // real viewport height — without it the page sits in the section
    // ScrollArea and every tab collapses to content height (the Documents
    // file viewer ended up as tall as its file list). Tabs scroll internally.
    fullBleed: true,
    title: (params, data) => {
      // Try to get caseId from params first
      const routeParams = params as { id?: string; caseId?: string }
      if (routeParams?.caseId) {
        return `${routeParams.caseId} • Details`
      }

      // Fallback: try to get it from data
      const caseData = data as { caseId?: string; id?: string }
      if (caseData?.caseId) {
        return `${caseData.caseId} • Details`
      }

      // Final fallback with customer id
      if (caseData?.id) {
        return `${caseData.id} • Details`
      }

      return 'Details'
    },
    tabKey: 'case',
    parentBreadcrumb: {
      label: 'Cases',
      href: (_params, orgSlug, customerId) =>
        `/${orgSlug}/internal/${customerId}/support/cases`
    }
  },
  '/internal/[id]/support/cases/[caseId]/emails/compose': {
    title: 'Compose Email',
    parentBreadcrumb: [
      {
        label: 'Cases',
        href: (_params, orgSlug, customerId) =>
          `/${orgSlug}/internal/${customerId}/support/cases`
      },
      {
        label: (params) => {
          const { caseId } = params as { caseId?: string }
          return caseId ? `${caseId}` : 'Case'
        },
        href: (params, orgSlug, customerId) => {
          const { caseId } = params as { caseId?: string }
          return `/${orgSlug}/internal/${customerId}/support/cases/${caseId}?tab=details`
        }
      }
    ]
  },
  '/internal/[id]/support/cases/[caseId]/emails/[emailId]': {
    title: 'Email',
    parentBreadcrumb: [
      {
        label: 'Cases',
        href: (_params, orgSlug, customerId) =>
          `/${orgSlug}/internal/${customerId}/support/cases`
      },
      {
        label: (params) => {
          const { caseId } = params as { caseId?: string }
          return caseId ? `${caseId}` : 'Case'
        },
        href: (params, orgSlug, customerId) => {
          const { caseId } = params as { caseId?: string }
          return `/${orgSlug}/internal/${customerId}/support/cases/${caseId}?tab=details`
        }
      }
    ]
  },
  '/internal/[id]/opportunities': {
    title: 'Opportunities'
  },
  '/internal/[id]/opportunities/[opportunityId]': {
    fullBleed: true,
    title: (params) => {
      const routeParams = params as { id?: string; opportunityId?: string }
      if (routeParams?.opportunityId) {
        return `${routeParams.opportunityId} • Summary`
      }
      return 'Summary'
    },
    tabKey: 'opportunity',
    parentBreadcrumb: {
      label: 'Opportunities',
      href: (_params, orgSlug, customerId) => {
        return `/${orgSlug}/internal/${customerId}/opportunities`
      }
    }
  },
  '/internal/[id]/support/work-orders': {
    title: 'Work Orders'
  },
  '/internal/[id]/support/work-orders/[workOrderId]': {
    title: (params, data) => {
      const routeParams = params as { id?: string; workOrderId?: string }
      if (routeParams?.workOrderId) {
        return `${routeParams.workOrderId} • Details`
      }

      const workOrderData = data as { workOrderId?: string; id?: string }
      if (workOrderData?.workOrderId) {
        return `${workOrderData.workOrderId} • Details`
      }

      if (workOrderData?.id) {
        return `${workOrderData.id} • Details`
      }

      return 'Details'
    },
    tabKey: 'work-order',
    parentBreadcrumb: {
      label: 'Work Orders',
      href: (_params, orgSlug, customerId) => {
        return `/${orgSlug}/internal/${customerId}/support/work-orders`
      }
    }
  }
}
